<template>
  <div class="w-full">
    <div class="relative">
      <!-- Custom Dropdown Button -->

      <button
        :id="id"
        :name="name"
        :disabled="isDisabled"
        type="button"
        @click="toggleDropdown"
        @blur="onBlur"
        class="block w-full rounded-md border border-gray-300 px-3 py-3 shadow-sm focus:ring focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-left bg-white"
        :class="{
          'border-red-500': error || showRequiredError,
          'opacity-50 cursor-not-allowed': loading || isDisabled,
          'border-blue-500': isOpen,
        }"
      >
        <!-- Multi-select display -->
        <div v-if="multiple && selectedOptions.length > 0" class="flex flex-wrap gap-1">
          <span
            v-for="option in selectedOptions"
            :key="option.value"
            class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
          >
            {{ option.label }}
            <button
              type="button"
              @click.stop="removeOption(option)"
              class="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-500 focus:outline-none"
            >
              <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
              </svg>
            </button>
          </span>
        </div>
        <!-- Single select display -->
        <span v-else-if="selectedOption" class="text-gray-900">
          {{ selectedOption.label }}
        </span>
        <span v-else class="text-gray-500">
          {{ placeholder || translate("Select an option") }}
        </span>

        <!-- Dropdown Arrow -->
        <svg
          class="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400"
          :class="{ 'rotate-180': isOpen }"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      <!-- Loading Spinner -->
      <div v-if="loading" class="absolute inset-y-0 right-8 flex items-center">
        <svg
          class="animate-spin h-4 w-4 text-gray-400"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          />
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
          />
        </svg>
      </div>

      <!-- Dropdown Menu -->
      <div
        v-if="isOpen"
        class="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
      >
        <div
          v-for="option in allOptions"
          :key="option.value"
          @click="selectOption(option)"
          class="px-3 py-2 text-sm text-gray-900 hover:bg-gray-100 cursor-pointer flex items-center"
          :class="{ 'bg-blue-50 text-blue-900': isSelected(option) }"
        >
          <!-- Checkbox for multi-select -->
          <div v-if="multiple" class="mr-2">
            <input
              type="checkbox"
              :checked="isSelected(option)"
              class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              @click.stop
            />
          </div>
          {{ option.label }}
        </div>

        <!-- Load More Button -->
        <div
          v-if="hasMorePages && !loading"
          @click.stop="handleLoadMore"
          class="px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 cursor-pointer border-t border-gray-200"
        >
          <div class="flex items-center justify-center">
            <span v-if="!loadingMore">{{ translate("Load More...") }}</span>
            <svg
              v-if="loadingMore"
              class="animate-spin h-4 w-4 text-blue-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>
          </div>
        </div>

        <!-- No options message -->
        <div
          v-if="allOptions.length === 0 && !loading"
          class="px-3 py-2 text-sm text-gray-500 text-center"
        >
          {{ translate("No options available") }}
        </div>
      </div>
    </div>

    <!-- Error Message -->
    <p v-if="computedErrorMessage" class="mt-1 text-sm text-red-600">
      {{ computedErrorMessage }}
    </p>
    <p v-else-if="hint" class="mt-1 text-sm" :class="{ 'text-red-600': error }">
      {{ hint }}
    </p>
  </div>
</template>

<script>
import { translations } from '@/mixins';
import BaseService from '@/services/base.service';

export default {
  name: 'BaseSelectWithLoadMore',
  mixins: [translations],
  props: {
    id: String,
    name: String,
    value: [String, Number, Boolean, Object, Array],
    placeholder: String,
    options: {
      type: Array,
      default: () => [],
    },
    getUrl: {
      type: String,
      required: false,
      default: null,
    },
    perPage: {
      type: Number,
      default: 10,
    },
    valueKey: {
      type: String,
      default: 'id',
    },
    labelKey: {
      type: String,
      default: 'name',
    },
    hint: String,
    error: Boolean,
    errorMessage: String,
    disabled: Boolean,
    required: Boolean,
    valid: {
      type: Boolean,
      default: undefined,
    },
    params: {
      type: Object,
      default: () => ({}),
    },
    multiple: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      allOptions: [],
      currentPage: 1,
      hasMorePages: false,
      loading: false,
      loadingMore: false,
      touched: false,
      localValid: this.valid,
      isOpen: false,
    };
  },
  computed: {
    localValue: {
      get () {
        // Ensure we always return an array for multi-select, even if value is null/undefined
        if (this.multiple) {
          return Array.isArray(this.value) ? this.value : [];
        }
        return this.value;
      },
      set (val) {
        this.$emit('input', val);
      },
    },
    showRequiredError () {
      return (
        this.required &&
        this.touched &&
        (!this.localValue ||
          (Array.isArray(this.localValue) && this.localValue.length == 0))
      );
    },
    computedErrorMessage () {
      if (!this.touched) return '';
      if (this.showRequiredError) {
        return this.errorMessage || this.translate('This field is required');
      }
      if (this.error && this.errorMessage) {
        return this.errorMessage;
      }
      return '';
    },
    isDisabled () {
      return this.disabled || this.loading;
    },
    selectedOption () {
      if (!this.localValue || this.multiple) return null;
      return this.allOptions.find((option) => this.normalizeValue(option.value) === this.normalizeValue(this.localValue));
    },
    selectedOptions () {
      if (!this.multiple || !this.localValue || !Array.isArray(this.localValue)) {
        return [];
      }
      return this.allOptions.filter((option) =>
        this.localValue.some((val) => this.normalizeValue(val) === this.normalizeValue(option.value)),
      );
    },
  },
  watch: {
    valid (newVal) {
      this.localValid = newVal;
    },
    value: {
      handler: async function (newVal) {
        await this.ensureSelectedValuePresent(newVal);
        this.updateValidity();
      },
      immediate: true,
    },
    getUrl: {
      handler: async function (newUrl, oldUrl) {
        if (newUrl !== oldUrl) {
          // Clear current options when URL changes
          this.allOptions = [];
          this.currentPage = 1;
          this.hasMorePages = false;

          // Load new data if URL is provided
          if (newUrl) {
            await this.loadInitialData();
          }
        }
      },
      immediate: false,
    },

  },

  methods: {
    /**
     * Normalizes a value for comparison by converting it to a consistent type.
     * Handles both string and number types to ensure proper comparison.
     */
    normalizeValue (value) {
      if (value === null || value === undefined) return value;
      // Convert to string for consistent comparison
      return String(value);
    },
    formatOptions (data) {
      return data
        .map((item) => {
          const value = this.getNestedValue(item, this.valueKey);
          const label = this.getNestedValue(item, this.labelKey);

          // Only include options with valid value and label
          if (value !== null && value !== undefined && label !== null && label !== undefined) {
            return {
              value,
              label,
              ...item,
            };
          }
          return null;
        })
        .filter(Boolean); // Remove null entries
    },

    getNestedValue (obj, path) {
      if (!path) return obj;

      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : null;
      }, obj);
    },

    async loadInitialData () {
      this.loading = true;
      try {
        const response = await this.fetchData(1);
        this.handleApiResponse(response);
        // After loading, ensure selected value is present only if not already in options
        if (this.localValue) {
          const needsFetching = this.multiple && Array.isArray(this.localValue)
            ? this.localValue.some((val) => !this.allOptions.find((option) => this.normalizeValue(option.value) === this.normalizeValue(val)))
            : !this.allOptions.find((option) => this.normalizeValue(option.value) === this.normalizeValue(this.localValue));

          if (needsFetching) {
            await this.ensureSelectedValuePresent(this.localValue);
          }
        }
      } catch (error) {
        this.$emit('error', error);
      } finally {
        this.loading = false;
      }
    },

    async loadMore () {
      if (this.loadingMore || !this.hasMorePages) return;

      this.loadingMore = true;
      try {
        const response = await this.fetchData(this.currentPage + 1);
        this.handleApiResponse(response, true);
      } catch (error) {
        this.$emit('error', error);
      } finally {
        this.loadingMore = false;
      }
    },

    async fetchData (page) {
      const baseService = new BaseService();
      const params = {
        size: this.perPage,
        page: page,
        ...this.params, // Merge with additional params from props
      };

      const fullUrl = baseService.baseURL + this.getUrl;
      return await baseService.get(fullUrl, params);
    },

    handleApiResponse (response, isLoadMore = false) {
      if (response && response.data) {
        const dataToFormat = response.data.data || response.data;

        const newOptions = this.formatOptions(dataToFormat);

        if (isLoadMore) {
          // Filter out already existing items before appending
          const existingValues = new Set(this.allOptions.map((option) => option.value));
          const filteredNewOptions = newOptions.filter((option) => !existingValues.has(option.value));
          this.allOptions = [...this.allOptions, ...filteredNewOptions];
          this.currentPage++;
        } else {
          // Replace options for initial load
          this.allOptions = newOptions;
          this.currentPage = 1;
        }

        // Handle pagination metadata based on the provided response structure
        if (response.data.meta && response.data.meta.pagination) {
          const pagination = response.data.meta.pagination;

          // Use page-based pagination logic
          // Only show load more if there are actually more pages
          this.hasMorePages = pagination.current_page < pagination.total_pages;
        } else {
          // Fallback: check if we got fewer items than per_page
          // If we get exactly perPage items, there might be more pages
          // If we get fewer than perPage items, we've reached the end
          // For the fallback case, we'll be conservative and only show load more
          // if we're on the first page and got exactly perPage items
          this.hasMorePages = this.currentPage === 1 && newOptions.length === this.perPage;
        }

        this.$emit('data-loaded', this.allOptions);
      }
    },

    onBlur () {
      this.touched = true;
      this.updateValidity();
    },

    toggleDropdown () {
      if (!this.isDisabled) {
        this.isOpen = !this.isOpen;
      }
    },

    selectOption (option) {
      if (this.multiple) {
        // Handle multi-select
        const currentValue = Array.isArray(this.localValue) ? [...this.localValue] : [];
        const optionIndex = currentValue.indexOf(option.value);

        if (optionIndex > -1) {
          // Remove if already selected
          currentValue.splice(optionIndex, 1);
        } else {
          // Add if not selected
          currentValue.push(option.value);
        }

        this.localValue = currentValue;
        this.$emit('input', currentValue);
      } else {
        // Handle single select
        this.localValue = option.value;
        this.$emit('input', option.value);
        this.isOpen = false;
      }

      this.touched = true;
      this.updateValidity();
    },

    removeOption (option) {
      if (this.multiple && Array.isArray(this.localValue)) {
        const currentValue = [...this.localValue];
        const optionIndex = currentValue.indexOf(option.value);

        if (optionIndex > -1) {
          currentValue.splice(optionIndex, 1);
          this.localValue = currentValue;
          this.$emit('input', currentValue);
          this.touched = true;
          this.updateValidity();
        }
      }
    },

    isSelected (option) {
      if (this.multiple) {
        return Array.isArray(this.localValue) && this.localValue.some((val) => this.normalizeValue(val) === this.normalizeValue(option.value));
      }
      return this.normalizeValue(this.localValue) === this.normalizeValue(option.value);
    },

    async handleLoadMore () {
      if (this.loadingMore || !this.hasMorePages) return;

      // Keep dropdown open while loading more
      await this.loadMore();
      // Dropdown stays open after loading more
    },

    handleClickOutside (event) {
      if (!this.$el.contains(event.target)) {
        this.isOpen = false;
      }
    },

    updateValidity () {
      let isValid = true;
      if (this.required) {
        isValid = !(
          !this.localValue ||
          (Array.isArray(this.localValue) && this.localValue.length === 0)
        );
      }
      this.localValid = isValid;
      this.$emit('update:valid', isValid);
      this.$emit('valid', isValid);
    },

    // Public method to refresh data
    async refresh () {
      if (this.getUrl) {
        await this.loadInitialData();
      } else if (this.options && this.options.length > 0) {
        this.allOptions = this.formatOptions(this.options);
        this.$emit('data-loaded', this.allOptions);
      }
    },

    /**
     * Ensures the selected value is present in allOptions. If not, fetches it using the single-item API and adds it.
     */
    async ensureSelectedValuePresent (value) {
      if (!value || !this.getUrl) return;

      if (this.multiple && Array.isArray(value)) {
        // Handle multiple values
        for (const val of value) {
          const isAlreadyPresent = this.allOptions.find((option) =>
            this.normalizeValue(option.value) === this.normalizeValue(val),
          );
          if (!isAlreadyPresent) {
            await this.fetchAndAddSingleValue(val);
          }
        }
      } else if (!this.multiple) {
        // Handle single value
        const isAlreadyPresent = this.allOptions.find((option) =>
          this.normalizeValue(option.value) === this.normalizeValue(value),
        );
        if (!isAlreadyPresent) {
          await this.fetchAndAddSingleValue(value);
        }
      }
    },

    async fetchAndAddSingleValue (value) {
      // Only proceed if getUrl is set and value is not present
      if (!this.getUrl || !value) return;

      // Double-check that the value is not already in the list before making the API call
      const isAlreadyPresent = this.allOptions.find((option) =>
        this.normalizeValue(option.value) === this.normalizeValue(value),
      );
      if (isAlreadyPresent) {
        return; // Item is already in the list, no need to fetch
      }

      // Try to construct the single-item URL
      let singleItemUrl = this.getUrl;
      if (!singleItemUrl.endsWith('/')) singleItemUrl += '/';
      singleItemUrl += value;

      try {
        const baseService = new BaseService();
        const fullUrl = baseService.baseURL + singleItemUrl;
        const response = await baseService.get(fullUrl);
        let itemData = response && response.data && (response.data.data || response.data);
        // If the API returns an array, take the first item
        if (Array.isArray(itemData)) itemData = itemData[0];
        if (itemData && itemData.id) {
          const formatted = this.formatOptions([itemData]);
          // Only add if formatted options are valid and not already present
          if (formatted.length > 0) {
            // Final check to ensure the item is not already in the list
            const finalCheck = this.allOptions.find((option) =>
              this.normalizeValue(option.value) === this.normalizeValue(value),
            );
            if (!finalCheck) {
              this.allOptions = [...this.allOptions, ...formatted];
            }
          }
        }
      } catch (e) {
        // Optionally handle error (e.g., item not found)
        console.warn('Failed to fetch single value:', value, e);
      }
    },
  },

  async mounted () {
    // Initialize with static options if provided
    if (this.options && this.options.length > 0) {
      this.allOptions = this.formatOptions(this.options);
      this.$emit('data-loaded', this.allOptions);
    }
    // Load initial data from API only if getUrl is provided
    if (this.getUrl) {
      await this.loadInitialData();
    }

    this.updateValidity();

    // Add click outside listener
    document.addEventListener('click', (event) => {
      if (!this.$el.contains(event.target)) {
        this.isOpen = false;
      }
    });
  },

  beforeDestroy () {
    // Remove click outside listener
    document.removeEventListener('click', this.handleClickOutside);
  },
};
</script>

<style scoped>
.load-more-option {
  color: #3b82f6;
  font-style: italic;
  background-color: #f8fafc;
}

.loading-more-option {
  color: #6b7280;
  font-style: italic;
  background-color: #f3f4f6;
}
</style>
